/**
 * UUID Analyzer Tool
 *
 * Parse UUID, detect version, extract timestamp, show variant, validate format.
 */

import { validate as uuidValidate, version as uuidVersion } from 'uuid';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

const VERSION_DESCRIPTIONS: Record<number, string> = {
  1: 'Time-based (MAC address + timestamp)',
  2: 'DCE Security',
  3: 'Name-based (MD5 hash)',
  4: 'Random',
  5: 'Name-based (SHA-1 hash)',
  6: 'Reordered time-based',
  7: 'Unix epoch time-based',
};

export const UuidAnalyzerTool: ITransformerTool = {
  id: 'uuid-analyzer',
  name: 'UUID Analyzer',
  description: 'Parse UUID to detect version, extract timestamp, show variant, and validate',
  version: '1.0.0',
  type: 'transformer',
  category: 'Utilities',
  keywords: ['uuid', 'guid', 'analyze', 'parse', 'version', 'timestamp', 'validate'],

  seo: {
    slug: 'uuid-analyzer',
    pageTitle: 'UUID Analyzer - Free Online UUID Parser | SWE DevTools',
    metaDescription: 'Analyze UUIDs to detect version (v1-v7), extract timestamps, show variant info, and validate format. Fast, private, runs in your browser.',
    keywords: ['uuid analyzer', 'uuid parser', 'uuid version detector', 'uuid timestamp', 'guid analyzer'],
    relatedTools: ['uuid-generator', 'nanoid-generator', 'random-string-generator'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.UUID, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const lines = input.trim().split('\n').map(l => l.trim()).filter(Boolean);
    const results: string[] = [];

    for (const line of lines) {
      results.push(analyzeUuid(line));
      if (lines.length > 1) results.push('');
    }

    return results.join('\n').trimEnd();
  },
};

function analyzeUuid(input: string): string {
  const uuid = input.toLowerCase().trim();
  const lines: string[] = [`UUID: ${uuid}`];

  // Validate format
  const formatValid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(uuid);
  if (!formatValid) {
    lines.push('✗ Invalid UUID format');
    lines.push('Expected: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx');
    return lines.join('\n');
  }

  const isValid = uuidValidate(uuid);
  lines.push(`Valid: ${isValid ? '✓ Yes' : '✗ No'}`);

  if (!isValid) return lines.join('\n');

  // Version
  const ver = uuidVersion(uuid);
  const desc = VERSION_DESCRIPTIONS[ver] ?? 'Unknown';
  lines.push(`Version: ${ver} (${desc})`);

  // Variant
  const variant = detectVariant(uuid);
  lines.push(`Variant: ${variant}`);

  // Timestamp extraction for v1 and v7
  if (ver === 1) {
    try {
      const ts = extractV1Timestamp(uuid);
      lines.push(`Timestamp: ${ts.toISOString()}`);
      lines.push(`Unix ms: ${ts.getTime()}`);
    } catch {
      lines.push('Timestamp: Could not extract');
    }
  }

  if (ver === 7) {
    try {
      const ts = extractV7Timestamp(uuid);
      lines.push(`Timestamp: ${ts.toISOString()}`);
      lines.push(`Unix ms: ${ts.getTime()}`);
    } catch {
      lines.push('Timestamp: Could not extract');
    }
  }

  // Node (MAC) for v1
  if (ver === 1) {
    const node = uuid.split('-')[4];
    lines.push(`Node (MAC): ${node?.match(/.{2}/g)?.join(':') ?? 'N/A'}`);
  }

  // Clock sequence for v1
  if (ver === 1) {
    const hex = uuid.replace(/-/g, '');
    const clockSeq = parseInt(hex.substring(16, 20), 16) & 0x3fff;
    lines.push(`Clock Sequence: ${clockSeq}`);
  }

  return lines.join('\n');
}

function detectVariant(uuid: string): string {
  const hex = uuid.replace(/-/g, '');
  const variantNibble = parseInt(hex[16], 16);

  if ((variantNibble & 0x8) === 0) return 'NCS (reserved)';
  if ((variantNibble & 0xc) === 0x8) return 'RFC 4122 / RFC 9562';
  if ((variantNibble & 0xe) === 0xc) return 'Microsoft (COM/DCOM)';
  return 'Reserved (future)';
}

function extractV1Timestamp(uuid: string): Date {
  const parts = uuid.split('-');
  const timeLow = parts[0];
  const timeMid = parts[1];
  const timeHi = parts[2].substring(1); // remove version digit

  const timestamp100Ns = BigInt('0x' + timeHi + timeMid + timeLow);
  // UUID epoch offset: Oct 15, 1582 → Unix epoch in 100ns intervals
  const uuidEpochOffset = BigInt('122192928000000000');
  const unixTime100Ns = timestamp100Ns - uuidEpochOffset;
  const unixTimeMs = Number(unixTime100Ns / BigInt(10000));

  return new Date(unixTimeMs);
}

function extractV7Timestamp(uuid: string): Date {
  const hex = uuid.replace(/-/g, '');
  const msHex = hex.substring(0, 12);
  return new Date(parseInt(msHex, 16));
}
